import { Currency } from "../utils/Currency";
import { UsageDetail } from "./Bill/DepositRecord/UsageDetail";
import { StaticAllocation } from "./Bill/DepositRecord/StaticAllocation";
import { v4 as uuidv4 } from "uuid";
import { DateUtil } from "../utils/DateUtil";
import { LocalDate, ChronoUnit } from "@js-joda/core";
import { RefundRecord } from "./RefundRecord";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);
import isBetween from "dayjs/plugin/isBetween";
dayjs.extend(isBetween);
export class DepositRecord {
    id;
    _amount;
    jsAmount;
    _currency;
    jsCurrency;
    _createdDate;
    jsCreatedDate;
    _insertedDate;
    jsInsertedDate;
    _effectiveDate;
    jsEffectiveDate;
    _clearingDate;
    jsClearingDate;
    _systemDate;
    jsSystemDate;
    _excessAppliedDate;
    jsExcessAppliedDate;
    _paymentMethod;
    jsPaymentMethod;
    _depositor;
    jsDepositor;
    _depositLocation;
    jsDepositLocation;
    _applyExcessToPrincipal;
    jsApplyExcessToPrincipal;
    _applyExcessAtTheEndOfThePeriod = false;
    jsApplyExcessAtTheEndOfThePeriod = false;
    _versionId = uuidv4();
    _dateChanged = LocalDate.now();
    initialized = false;
    depositRecords;
    active = true;
    _metadata;
    _usageDetails = [];
    _refunds = [];
    _staticAllocation;
    constructor(params) {
        // console.log("params in deposit record", params);
        if (params.id) {
            this.id = params.id;
        }
        else {
            if (params.metadata?.type === "auto_close") {
                this.id = DepositRecord.generateUniqueId(params.sequence, "AUTO_CLOSE");
            }
            else if (params.metadata?.type === "adhoc_refund") {
                this.id = DepositRecord.generateUniqueId(params.sequence, "ADHOC_REFUND");
            }
            else {
                this.id = DepositRecord.generateUniqueId(params.sequence);
            }
        }
        if (params.active !== undefined) {
            this.active = params.active;
        }
        this.amount = Currency.of(params.amount);
        this.currency = params.currency;
        this.insertedDate = LocalDate.now();
        this.effectiveDate = DateUtil.normalizeDate(params.effectiveDate);
        if (params.clearingDate) {
            //console.log(`clearing date is passed as ${params.clearingDate}`);
            this.clearingDate = DateUtil.normalizeDate(params.clearingDate);
        }
        if (params.systemDate) {
            this.systemDate = params.systemDate;
        }
        else {
            this.systemDate = DateUtil.todayWithTime();
        }
        if (params.createdDate) {
            this.createdDate = DateUtil.normalizeDate(params.createdDate);
        }
        else {
            this.createdDate = LocalDate.now();
        }
        this.paymentMethod = params.paymentMethod;
        this.depositor = params.depositor;
        this.depositLocation = params.depositLocation;
        this.metadata = params.metadata;
        this.applyExcessToPrincipal = params.applyExcessToPrincipal || false;
        if (this.applyExcessToPrincipal && !params.excessAppliedDate) {
            this.excessAppliedDate = this.effectiveDate;
        }
        else if (params.excessAppliedDate) {
            this.excessAppliedDate = DateUtil.normalizeDate(params.excessAppliedDate);
        }
        this.usageDetails = params.usageDetails || [];
        // this.unusedAmount = params.unusedAmount ? params.unusedAmount : 0;
        if (params.staticAllocation) {
            this.staticAllocation = params.staticAllocation;
        }
        if (params.applyExcessAtTheEndOfThePeriod !== undefined) {
            this.applyExcessAtTheEndOfThePeriod = params.applyExcessAtTheEndOfThePeriod;
        }
        if (params.depositRecords) {
            this.depositRecords = params.depositRecords;
        }
        this.refunds = params.refunds ?? [];
        this.updateJsValues();
        this.initialized = true;
    }
    get isAutoClose() {
        return this.metadata?.type === "auto_close";
    }
    get isAdhocRefund() {
        return this.metadata?.type === "adhoc_refund";
    }
    get adhocBalanceImpacting() {
        return this.metadata?.balanceImpacting ?? false;
    }
    get versionId() {
        return this._versionId;
    }
    get dateChanged() {
        return this._dateChanged;
    }
    versionChanged() {
        if (this.initialized === true) {
            this._dateChanged = LocalDate.now();
            this._versionId = uuidv4();
            if (this.depositRecords) {
                this.depositRecords.versionChanged();
            }
        }
    }
    get applyExcessAtTheEndOfThePeriod() {
        return this._applyExcessAtTheEndOfThePeriod;
    }
    set applyExcessAtTheEndOfThePeriod(value) {
        this._applyExcessAtTheEndOfThePeriod = value;
        this.versionChanged();
    }
    clone() {
        // console.log("json", this.json);
        // console.log("usage details", this.usageDetails);
        return new DepositRecord(this.json);
    }
    static rehydrateFromJSON(json) {
        return new DepositRecord(json);
    }
    addUsageDetail(detail) {
        // ignore if all amounts are zero
        if (detail.allocatedPrincipal.isZero() && detail.allocatedInterest.isZero() && detail.allocatedFees.isZero()) {
            console.warn("Ignoring usage detail with all zero amounts");
            return;
        }
        this._usageDetails.push(detail);
        // order by billDueDate using LocalDate and ChronoUnit
        this._usageDetails = this._usageDetails.sort((a, b) => ChronoUnit.DAYS.between(a.billDueDate, b.billDueDate));
        this.versionChanged();
    }
    removeUsageDetail(index) {
        if (index < 0 || index >= this._usageDetails.length) {
            console.warn(`Invalid index ${index} for removeUsageDetail. Array length: ${this._usageDetails.length}`);
            return;
        }
        this._usageDetails.splice(index, 1);
        this.versionChanged();
    }
    resetUsageDetails() {
        // if usage detail has a balance modification
        // we want to mark it for removal so
        // this will help LendPeak class do do the cleanup
        // in amortization class and remove system generated
        // balance modifications
        this._usageDetails.forEach((detail) => {
            if (detail.balanceModification) {
                detail.balanceModification.markedForRemoval = true;
            }
        });
        this._usageDetails = [];
        this.versionChanged();
    }
    clearHistory() {
        this.resetUsageDetails();
        //  this.unusedAmount = Currency.Zero();
        this.versionChanged();
    }
    removeRefund(refund) {
        const idx = this._refunds.indexOf(refund);
        if (idx >= 0) {
            this._refunds.splice(idx, 1);
            // Only add amount back if refund is currently active
            // (inactive refunds have already had their amount added back)
            if (refund.active) {
                this.amount = this.amount.add(refund.amount); // give cash back
            }
            this.versionChanged();
        }
    }
    /** Sum of amounts on all _active_ refund rows */
    get activeRefundAmount() {
        if (!this.refunds?.length)
            return Currency.Zero();
        return this.refunds.filter((r) => r.active).reduce((acc, r) => acc.add(r.amount), Currency.Zero());
    }
    /** convenience for Angular tables */
    get jsActiveRefundAmount() {
        return this.activeRefundAmount.toNumber();
    }
    get staticAllocation() {
        return this._staticAllocation;
    }
    set staticAllocation(value) {
        if (value instanceof StaticAllocation) {
            this._staticAllocation = value;
        }
        else if (value) {
            this._staticAllocation = new StaticAllocation(value);
        }
        else {
            this._staticAllocation = undefined;
        }
        this.versionChanged();
    }
    get amount() {
        return this._amount;
    }
    set amount(value) {
        this._amount = value;
        this.jsAmount = value.toNumber();
        this.versionChanged();
    }
    get currency() {
        return this._currency;
    }
    set currency(value) {
        this._currency = value;
        this.jsCurrency = value;
        this.versionChanged();
    }
    get createdDate() {
        return this._createdDate;
    }
    set createdDate(value) {
        this._createdDate = DateUtil.normalizeDate(value);
        this.jsCreatedDate = DateUtil.normalizeDateToJsDate(this._createdDate);
        this.versionChanged();
    }
    get insertedDate() {
        return this._insertedDate;
    }
    set insertedDate(value) {
        this._insertedDate = DateUtil.normalizeDate(value);
        this.jsInsertedDate = DateUtil.normalizeDateToJsDate(this._insertedDate);
        this.versionChanged();
    }
    get effectiveDate() {
        return this._effectiveDate;
    }
    set effectiveDate(value) {
        const normalizedDate = DateUtil.normalizeDate(value);
        this._effectiveDate = normalizedDate;
        this.jsEffectiveDate = DateUtil.normalizeDateToJsDate(normalizedDate);
        this.versionChanged();
    }
    get clearingDate() {
        return this._clearingDate;
    }
    set clearingDate(value) {
        if (!value) {
            this._clearingDate = undefined;
            this.jsClearingDate = undefined;
        }
        else {
            const normalizedDate = DateUtil.normalizeDate(value);
            this._clearingDate = normalizedDate;
            this.jsClearingDate = DateUtil.normalizeDateToJsDate(normalizedDate);
        }
        this.versionChanged();
    }
    get systemDate() {
        return this._systemDate;
    }
    set systemDate(value) {
        this._systemDate = DateUtil.normalizeDateTime(value);
        this.jsSystemDate = DateUtil.normalizeDateTimeToJsDate(this._systemDate);
        this.versionChanged();
    }
    get paymentMethod() {
        return this._paymentMethod;
    }
    get depositor() {
        return this._depositor;
    }
    get depositLocation() {
        return this._depositLocation;
    }
    get applyExcessToPrincipal() {
        return this._applyExcessToPrincipal;
    }
    set applyExcessToPrincipal(value) {
        this._applyExcessToPrincipal = value;
        this.jsApplyExcessToPrincipal = value;
        this.versionChanged();
    }
    get excessAppliedDate() {
        return this._excessAppliedDate;
    }
    set excessAppliedDate(value) {
        if (!value) {
            this._excessAppliedDate = undefined;
            this.jsExcessAppliedDate = undefined;
            return;
        }
        const normalizedDate = DateUtil.normalizeDate(value);
        this._excessAppliedDate = normalizedDate;
        this.jsExcessAppliedDate = DateUtil.normalizeDateToJsDate(normalizedDate);
        this.versionChanged();
    }
    get excessAmountApplied() {
        // find in the usage details the principal prepayment
        const principalPrepayment = this.usageDetails.find((detail) => detail.billId === "Principal Prepayment");
        if (principalPrepayment) {
            return Currency.of(principalPrepayment.allocatedPrincipal);
        }
        else {
            return Currency.Zero();
        }
    }
    get usageDetails() {
        // verify type and if incorrect, inflate the object
        this._usageDetails = this._usageDetails.map((detail) => {
            if (detail instanceof UsageDetail) {
                return detail;
            }
            return new UsageDetail(detail);
        });
        return this._usageDetails;
    }
    get metadata() {
        return this._metadata;
    }
    set metadata(value) {
        this._metadata = value;
        this.versionChanged();
    }
    set paymentMethod(value) {
        this._paymentMethod = value;
        this.jsPaymentMethod = value;
        this.versionChanged();
    }
    set depositor(value) {
        this._depositor = value;
        this.jsDepositor = value;
        this.versionChanged();
    }
    set depositLocation(value) {
        this._depositLocation = value;
        this.jsDepositLocation = value;
        this.versionChanged();
    }
    set usageDetails(value) {
        // console.log("usage details", value);
        // verify type and if incorrect, inflate the object
        this._usageDetails = value.map((detail) => {
            if (detail instanceof UsageDetail) {
                return detail;
            }
            return new UsageDetail(detail);
        });
        this.versionChanged();
    }
    get jsUnusedAmount() {
        return this.unusedAmount.toNumber();
    }
    get unusedAmount() {
        if (!this.active)
            return Currency.Zero();
        if (this.isAdhocRefund)
            return Currency.Zero();
        const allocated = this.usageDetails.reduce((tot, d) => tot.add(d.allocatedPrincipal).add(d.allocatedInterest).add(d.allocatedFees), Currency.Zero());
        const remainder = this.amount.subtract(allocated);
        return remainder.isNegative() ? Currency.Zero() : remainder;
    }
    /* ------------------------------------------------------------------ */
    /*  Refund helpers                                                    */
    /* ------------------------------------------------------------------ */
    get refunds() {
        /* inflate if deserialized */
        this._refunds = this._refunds.map((r) => (r instanceof RefundRecord ? r : new RefundRecord(r)));
        return this._refunds;
    }
    set refunds(value) {
        /* same “inflate + sort” pattern as other collections */
        this._refunds = value.map((r) => (r instanceof RefundRecord ? r : new RefundRecord(r)));
        this._refunds.forEach((r) => (r.depositRecord = this));
        this.versionChanged();
    }
    /** Total refunded so far (active refunds only). */
    get refundedAmount() {
        return this._refunds.reduce((sum, r) => (r.active ? sum.add(r.amount) : sum), Currency.Zero());
    }
    /** Primary API — create a refund and auto-adjust deposit. */
    addRefund(refund) {
        if (refund.currency !== this.currency) {
            console.warn(`Refund currency (${refund.currency}) != deposit currency (${this.currency}). Proceeding anyway.`);
        }
        refund.depositRecord = this;
        this._refunds.push(refund);
        /* ↓↓↓ core business rule ↓↓↓
           Refunds *reduce* the deposit’s available cash. */
        this.amount = this.amount.subtract(refund.amount);
        this.versionChanged();
    }
    /** Remove by index (for UI edits) */
    removeRefundAt(index) {
        if (index < 0 || index >= this._refunds.length)
            return;
        const refund = this._refunds[index];
        this._refunds.splice(index, 1);
        /* Re-inflate deposit amount so net stays consistent */
        this.amount = this.amount.add(refund.amount);
        this.versionChanged();
    }
    updateModelValues() {
        this.amount = Currency.of(this.jsAmount || 0);
        this.currency = this.jsCurrency || "";
        this.createdDate = DateUtil.normalizeDate(this.jsCreatedDate);
        this.insertedDate = DateUtil.normalizeDate(this.jsInsertedDate);
        this.effectiveDate = DateUtil.normalizeDate(this.jsEffectiveDate);
        this.systemDate = DateUtil.normalizeDateTime(this.jsSystemDate);
        this.clearingDate = this.jsClearingDate ? DateUtil.normalizeDate(this.jsClearingDate) : undefined;
        this.paymentMethod = this.jsPaymentMethod;
        this.depositor = this.jsDepositor;
        this.depositLocation = this.jsDepositLocation;
        this.applyExcessToPrincipal = this.jsApplyExcessToPrincipal || false;
        this.excessAppliedDate = this.jsExcessAppliedDate ? DateUtil.normalizeDate(this.jsExcessAppliedDate) : undefined;
        //  this.unusedAmount = Currency.of(this.jsUnusedAmount || 0);
        if (this.staticAllocation) {
            this.staticAllocation.updateModelValues();
        }
        this.applyExcessAtTheEndOfThePeriod = this.jsApplyExcessAtTheEndOfThePeriod || false;
        this.refunds.forEach((r) => r.updateModelValues());
    }
    get balanceModifications() {
        return this.usageDetails.filter((detail) => detail.balanceModification).map((detail) => detail.balanceModification);
    }
    updateJsValues() {
        this.jsAmount = this.amount.toNumber();
        this.jsCurrency = this.currency;
        this.jsCreatedDate = DateUtil.normalizeDateToJsDate(this.createdDate);
        this.jsInsertedDate = DateUtil.normalizeDateToJsDate(this.insertedDate);
        this.jsEffectiveDate = DateUtil.normalizeDateToJsDate(this.effectiveDate);
        this.jsSystemDate = DateUtil.normalizeDateTimeToJsDate(this.systemDate);
        if (this.clearingDate) {
            this.jsClearingDate = DateUtil.normalizeDateToJsDate(this.clearingDate);
        }
        else {
            this.jsClearingDate = undefined;
        }
        this.jsPaymentMethod = this.paymentMethod;
        this.jsDepositor = this.depositor;
        this.jsDepositLocation = this.depositLocation;
        this.jsApplyExcessToPrincipal = this.applyExcessToPrincipal;
        if (this.excessAppliedDate) {
            this.jsExcessAppliedDate = DateUtil.normalizeDateToJsDate(this.excessAppliedDate);
        }
        else {
            this.jsExcessAppliedDate = undefined;
        }
        if (this.staticAllocation) {
            this.staticAllocation.updateJsValues();
        }
        this.jsApplyExcessAtTheEndOfThePeriod = this.applyExcessAtTheEndOfThePeriod;
        this.refunds.forEach((r) => r.updateJsValues());
    }
    static generateUniqueId(sequence, prefix = "DEPOSIT") {
        // Simple unique ID generator (you can replace this with UUID if needed)
        const sequencePrefix = sequence !== undefined ? `${sequence}-` : "";
        return `${prefix}-` + sequencePrefix + Math.random().toString(36).substr(2, 9);
    }
    removeStaticAllocation() {
        this._staticAllocation = undefined;
        this.versionChanged();
    }
    /** convenience fields for the datatable */
    get allocatedPrincipal() {
        return this.summary.totalPrincipal;
    }
    get allocatedInterest() {
        return this.summary.totalInterest;
    }
    get allocatedFees() {
        return this.summary.totalFees;
    }
    get allocatedTotal() {
        return this.summary.totalPrincipal.add(this.summary.totalInterest).add(this.summary.totalFees);
    }
    get summary() {
        let total = this.amount;
        let totalUnused = this.unusedAmount;
        let totalInterest = Currency.of(0);
        let totalFees = Currency.of(0);
        let totalPrincipal = Currency.of(0);
        this.usageDetails.forEach((detail) => {
            totalInterest = totalInterest.add(detail.allocatedInterest);
            totalFees = totalFees.add(detail.allocatedFees);
            totalPrincipal = totalPrincipal.add(detail.allocatedPrincipal);
        });
        return {
            total: total,
            totalInterest: totalInterest,
            totalFees: totalFees,
            totalPrincipal: totalPrincipal,
            totalUnused: totalUnused,
        };
    }
    toJSON() {
        return this.json;
    }
    get json() {
        const toReturn = {
            id: this.id,
            amount: this.amount.toNumber(),
            currency: this.currency,
            createdDate: DateUtil.normalizeDateToJsDate(this.createdDate),
            insertedDate: DateUtil.normalizeDateToJsDate(this.insertedDate),
            effectiveDate: DateUtil.normalizeDateToJsDate(this.effectiveDate),
            clearingDate: this.clearingDate ? DateUtil.normalizeDateToJsDate(this.clearingDate) : undefined,
            systemDate: DateUtil.normalizeDateTimeToJsDate(this.systemDate),
            paymentMethod: this.paymentMethod,
            depositor: this.depositor,
            depositLocation: this.depositLocation,
            applyExcessToPrincipal: this.applyExcessToPrincipal,
            excessAppliedDate: this.excessAppliedDate ? DateUtil.normalizeDateToJsDate(this.excessAppliedDate) : undefined,
            usageDetails: this.usageDetails.map((detail) => {
                // console.log("Inside deposit record detail", detail);
                // console.log('"Inside deposit record detail.json', detail.json);
                return detail.json;
            }),
            unusedAmount: this.unusedAmount.toNumber(),
            metadata: this.metadata,
            active: this.active,
            staticAllocation: this.staticAllocation ? this.staticAllocation.json : undefined,
            applyExcessAtTheEndOfThePeriod: this.applyExcessAtTheEndOfThePeriod,
        };
        if (this.refunds) {
            toReturn.refunds = this.refunds.map((r) => r.json);
        }
        return toReturn;
    }
    toCode() {
        return `new DepositRecord({
      id: "${this.id}",
      amount: ${this.amount.toNumber()},
      currency: "${this.currency}",
      createdDate: DateUtil.normalizeDate("${this.createdDate.toString()}"),
      insertedDate: DateUtil.normalizeDate("${this.insertedDate.toString()}"),
      effectiveDate: DateUtil.normalizeDate("${this.effectiveDate.toString()}"),
      clearingDate: ${this.clearingDate ? `DateUtil.normalizeDate("${this.clearingDate.toString()}")` : "undefined"},
      systemDate: DateUtil.normalizeDateTime("${this.systemDate.toString()}"),
      paymentMethod: ${this.paymentMethod ? `"${this.paymentMethod}"` : "undefined"},
      depositor: ${this.depositor ? `"${this.depositor}"` : "undefined"},
      depositLocation: ${this.depositLocation ? `"${this.depositLocation}"` : "undefined"},
      applyExcessToPrincipal: ${this.applyExcessToPrincipal},
      excessAppliedDate: ${this.excessAppliedDate ? `DateUtil.normalizeDate("${this.excessAppliedDate.toString()}")` : "undefined"},
      unusedAmount: ${this.unusedAmount.toNumber()},
      metadata: ${JSON.stringify(this.metadata)},
      active: ${this.active},
      staticAllocation: ${this.staticAllocation ? this.staticAllocation.toCode() : "undefined"},
      applyExcessAtTheEndOfThePeriod: ${this.applyExcessAtTheEndOfThePeriod},
      refunds: [
    ${this.refunds.map((r) => r.toCode()).join(",\n    ")}
  ],
    })`;
    }
}
